import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LuArrowRight, LuPlay, LuFlame,
  LuSmartphone, LuRadio, LuServer, LuLayoutDashboard
} from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import './HowItWorks.css';

const STEPS = [
  {
    n: 1,
    title: 'Create your account',
    body: 'Sign up at textlinksms.com/signup. 300 free messages, no credit card required. Dashboard ready in under a minute.',
    time: '30 sec'
  },
  {
    n: 2,
    title: 'Install the Android app',
    body: 'Download our APK on a spare Android phone (any model under $100 works). The app runs in the background as your sender.',
    time: '2 min'
  },
  {
    n: 3,
    title: 'Pair the phone',
    body: 'Open Your Devices in the dashboard and scan the QR code with the phone. The SIM connects automatically — no typing.',
    time: '30 sec'
  },
  {
    n: 4,
    title: 'Connect your stack',
    body: 'Use the dashboard, REST API, or install the GoHighLevel marketplace app. Outbound goes through your SIM, replies sync back.',
    time: '1–5 min'
  }
];

export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="How It Works — Android SMS Gateway Setup"
        description="See exactly how TextLink turns a spare Android phone into an SMS gateway. Phone, SIM, server, your CRM — five-minute setup, no A2P paperwork."
        canonical="/how-textlink-works"
      />
      <HeaderOne />

      <main className="hw">
        <div className="hw-shell">

          {/* ---- HERO ---- */}
          <header className="hw-hero">
            <span className="hw-eyebrow">How it works</span>
            <h1 className="hw-h1">
              Your phone.<br />
              Your SIM. <span className="hw-h1-accent">Your SMS.</span>
            </h1>
            <p className="hw-lead">
              TextLink turns a spare Android into an SMS gateway. Real SIM, real device, real delivery — no aggregators, no A2P, no markup. Here's exactly how the pipeline works.
            </p>
            <div className="hw-cta-row">
              <button className="hw-btn hw-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <a className="hw-btn hw-btn--ghost" href="https://www.youtube.com/@TextLink" target="_blank" rel="noopener noreferrer">
                <LuPlay size={12} strokeWidth={2} fill="currentColor" /> Watch demo
              </a>
            </div>
          </header>

          {/* ---- THE PIPELINE ---- */}
          <section className="hw-section">
            <h2 className="hw-h2">The pipeline</h2>
            <p className="hw-sub">
              Four moving parts. Once configured, messages flow end-to-end automatically.
            </p>

            <div className="hw-flow">
              <div className="hw-flow-node">
                <div className="hw-flow-icon"><LuSmartphone size={22} strokeWidth={1.75} /></div>
                <h3>Your Android phone</h3>
                <p>Runs the TextLink app in the background. Plugged in, on Wi-Fi.</p>
              </div>
              <div className="hw-flow-arrow" aria-hidden="true">
                <LuArrowRight size={18} strokeWidth={1.75} />
              </div>
              <div className="hw-flow-node">
                <div className="hw-flow-icon"><LuRadio size={22} strokeWidth={1.75} /></div>
                <h3>Your SIM card</h3>
                <p>Sends SMS over the carrier's normal P2P channel. No A2P registration.</p>
              </div>
              <div className="hw-flow-arrow" aria-hidden="true">
                <LuArrowRight size={18} strokeWidth={1.75} />
              </div>
              <div className="hw-flow-node">
                <div className="hw-flow-icon"><LuServer size={22} strokeWidth={1.75} /></div>
                <h3>TextLink server</h3>
                <p>Routes messages, syncs status, handles webhooks, dedupes contacts.</p>
              </div>
              <div className="hw-flow-arrow" aria-hidden="true">
                <LuArrowRight size={18} strokeWidth={1.75} />
              </div>
              <div className="hw-flow-node">
                <div className="hw-flow-icon"><LuLayoutDashboard size={22} strokeWidth={1.75} /></div>
                <h3>Your CRM or app</h3>
                <p>Dashboard, GoHighLevel, REST API, Zapier, Make, n8n — your choice.</p>
              </div>
            </div>

            <p className="hw-note">
              Inbound flows the same way in reverse. SMS arrives at your SIM → phone forwards to TextLink → TextLink delivers to your CRM's inbox.
            </p>
          </section>

          {/* ---- STEPS ---- */}
          <section className="hw-section">
            <h2 className="hw-h2">Live in five minutes</h2>
            <p className="hw-sub">
              Whole flow takes about 5 minutes for the first SIM. Each additional SIM is ~30 seconds.
            </p>

            <ol className="hw-steps">
              {STEPS.map((s) => (
                <li key={s.n} className="hw-step">
                  <div className="hw-step-num">{s.n}</div>
                  <div className="hw-step-body">
                    <div className="hw-step-head">
                      <h3 className="hw-step-title">{s.title}</h3>
                      <span className="hw-step-time">{s.time}</span>
                    </div>
                    <p className="hw-step-text">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* ---- VIDEO ---- */}
          <section className="hw-section">
            <h2 className="hw-h2">See it in action</h2>
            <p className="hw-sub">Full setup walkthrough — under 10 minutes start to finish.</p>
            <div className="hw-video">
              <iframe
                src="https://www.youtube.com/embed/koabZVvF8R0"
                title="TextLink Setup Walkthrough"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="hw-foot">
              More tutorials on the{' '}
              <a href="https://www.youtube.com/@TextLink" target="_blank" rel="noopener noreferrer">YouTube channel ↗</a>
            </p>
          </section>

          {/* ---- WARMUP ---- */}
          <section className="hw-section">
            <div className="hw-warmup">
              <div className="hw-warmup-text">
                <span className="hw-chip"><LuFlame size={12} strokeWidth={2} /> Built in</span>
                <h2 className="hw-h2">SMS warmup, automated</h2>
                <p className="hw-sub">
                  New SIMs ramp gradually over 21 days so carriers trust them before you scale. Without warmup, fresh numbers are far more likely to be flagged and disabled.
                </p>
                <ul className="hw-warmup-list">
                  <li><strong>Days 1–7</strong><span>Establish baseline — 10–20 SMS/day to other warmed numbers.</span></li>
                  <li><strong>Days 8–14</strong><span>Scale up while the system watches delivery rate.</span></li>
                  <li><strong>Days 15–21</strong><span>Full volume, reputation protected.</span></li>
                </ul>
              </div>
            </div>
          </section>

          {/* ---- CTA ---- */}
          <section className="hw-cta">
            <h2 className="hw-h2">Try it with your own SIM.</h2>
            <p className="hw-sub">Five minutes, no credit card, no paperwork.</p>
            <div className="hw-cta-row">
              <button className="hw-btn hw-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="hw-btn hw-btn--ghost" to="/pricing">See pricing</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
